import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield, FileText, RotateCcw, AlertTriangle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

const pages = {
  privacy: {
    icon: <Shield className="w-6 h-6" strokeWidth={1.5} />,
    title: "Privacy Policy",
    titleHi: "गोपनीयता नीति",
    content: `Last updated: March 2026

**Information We Collect**
We collect personal information that you provide when using our services, including:
- Name and birth date (for astrology readings)
- Gender (optional)
- Questions you ask during chat sessions
- Payment information (processed securely via Razorpay)

**How We Use Your Information**
- To provide personalized astrology readings and chat sessions
- To process payments securely
- To improve our services and user experience
- To communicate with you about your sessions

**Data Security**
We implement appropriate security measures to protect your personal information. Payment data is processed through Razorpay's secure payment gateway and is not stored on our servers.

**Data Retention**
Chat session data is retained for service improvement purposes. You can request deletion of your data by contacting us.

**Third-Party Services**
We use Razorpay for payment processing. Their privacy policy applies to payment transactions.

**Contact Us**
For privacy-related inquiries, please contact us at support.vishuastro.com@gmail.com`
  },
  terms: {
    icon: <FileText className="w-6 h-6" strokeWidth={1.5} />,
    title: "Terms & Conditions",
    titleHi: "नियम और शर्तें",
    content: `Last updated: March 2026

**Acceptance of Terms**
By using our platform, you agree to these terms and conditions.

**Service Description**
We provide astrology-based chat sessions for entertainment and guidance purposes. Each session lasts 3 minutes and costs ₹49.

**Payment Terms**
- Sessions are unlocked upon successful payment of ₹49
- Each payment grants access to a 3-minute chat session
- Payments are processed securely via Razorpay

**User Responsibilities**
- You must provide accurate personal information
- You must be at least 18 years old to use our services
- You agree not to misuse or abuse the chat system

**Disclaimer**
Our astrology readings are for entertainment and general guidance purposes only. They should not be considered as professional advice for medical, legal, financial, or other critical decisions.

**Limitation of Liability**
We are not responsible for any decisions made based on the readings provided. Use the service at your own discretion.

**Modifications**
We reserve the right to modify these terms at any time. Continued use constitutes acceptance of modified terms.`
  },
  refund: {
    icon: <RotateCcw className="w-6 h-6" strokeWidth={1.5} />,
    title: "Refund Policy",
    titleHi: "रिफंड नीति",
    content: `Last updated: March 2026

**Refund Eligibility**
Since our service provides instant digital content (live chat sessions), refunds are generally not available once a session has been started.

**When Refunds May Be Issued**
- Technical failure preventing the chat session from functioning
- Payment charged but session not activated
- Duplicate payment for the same session

**How to Request a Refund**
Contact us within 24 hours of the payment with:
- Your name and email
- Payment receipt or transaction ID
- Reason for refund request

**Refund Processing**
Approved refunds will be processed within 7-10 business days to the original payment method.

**Non-Refundable Cases**
- Sessions that were successfully started and used
- Dissatisfaction with reading content (as it is for entertainment purposes)
- Sessions where the timer expired without usage

**Contact for Refunds**
Email: support.vishuastro.com@gmail.com`
  },
  disclaimer: {
    icon: <AlertTriangle className="w-6 h-6" strokeWidth={1.5} />,
    title: "Disclaimer",
    titleHi: "अस्वीकरण",
    content: `**Entertainment Purpose**
All astrology readings, personality analyses, and predictions provided on this platform are for entertainment and general guidance purposes only.

**Not Professional Advice**
The content provided should not be considered as professional advice. Do not make important life decisions based solely on astrology readings.

**No Guarantees**
We do not guarantee the accuracy of any predictions, personality analyses, or guidance provided during chat sessions.

**User Discretion**
Users are advised to use their own judgment and discretion when interpreting the readings. The platform is designed to provide an engaging and entertaining experience.

**Health & Legal Disclaimer**
This service is not a substitute for professional medical, legal, financial, or psychological advice. If you need professional help, please consult qualified professionals.

**Age Requirement**
This service is intended for users aged 18 and above.`
  },
  contact: {
    icon: <Mail className="w-6 h-6" strokeWidth={1.5} />,
    title: "Contact Us",
    titleHi: "संपर्क करें",
    content: `**Get in Touch**
We'd love to hear from you! Whether you have questions, feedback, or need assistance, reach out to us.

**Email Support**
support.vishuastro.com@gmail.com

**Response Time**
We typically respond within 24-48 hours.

**For Payment Issues**
Include your transaction ID and the name used during registration.

**For Technical Issues**
Please describe the issue in detail, including:
- Device and browser you're using
- Steps to reproduce the issue
- Any error messages you see

**Business Inquiries**
For partnership or business-related inquiries, please email support.vishuastro.com@gmail.com

**Feedback**
We value your feedback! Let us know how we can improve your experience.`
  }
};

export default function LegalPage() {
  const { page } = useParams();
  const { language } = useLanguage();
  const pageData = pages[page];

  if (!pageData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400">Page not found</p>
          <Link to="/">
            <Button variant="ghost" className="mt-4 text-amber-600">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid={`legal-page-${page}`}>
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gray-100 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/">
            <Button variant="ghost" size="icon" className="text-gray-400 hover:text-gray-900 hover:bg-gray-50">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </Link>
          <div className="flex items-center gap-2 text-amber-600">{pageData.icon}</div>
          <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
            {language === "hi" ? pageData.titleHi : pageData.title}
          </h1>
        </div>
      </header>

      <motion.main initial="hidden" animate="visible" variants={fadeIn} className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card rounded-3xl p-8 md:p-12">
          <div className="prose prose-sm max-w-none">
            {pageData.content.split("\n").map((line, i) => {
              if (line.startsWith("**") && line.endsWith("**")) {
                return <h3 key={i} className="text-gray-900 text-lg font-semibold mt-6 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{line.replace(/\*\*/g, "")}</h3>;
              }
              if (line.startsWith("- ")) {
                return <li key={i} className="text-gray-600 ml-4 mb-1">{line.slice(2)}</li>;
              }
              if (line.trim() === "") return <br key={i} />;
              // Make emails clickable
              if (line.includes("@gmail.com")) {
                const email = line.match(/[\w.-]+@[\w.-]+/)?.[0];
                if (email) return <p key={i} className="text-gray-600 leading-relaxed mb-2"><a href={`mailto:${email}`} className="text-amber-600 hover:text-amber-700 underline">{email}</a></p>;
              }
              return <p key={i} className="text-gray-600 leading-relaxed mb-2">{line}</p>;
            })}
          </div>
        </div>
      </motion.main>
    </div>
  );
}
